import React, { useEffect, useState } from 'react';
import { init } from 'goaffpro-client';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CONFIG } from '../../../config/mainConfig';
import { UserLocalStorageKeysEnum } from '../../../interfaces/user.interface';
import { AppDispatch, RootState } from '../../../store/store';
import { getURLSearchParams } from '../../../helper/helper';
import GeneralSignUp from './GeneralSignUp';
import Loader from '../../../components/Common/Loader';
import { getUserAuthSession } from '../../Builder/components/SignUp/SignUp.service';
import { autoSubscribeOnThePlan, autoSubscribeOnThePlanFromInvitationToken } from '../../Builder/services/auth.service';
import { autoFetchUserDetails, forceFetchUserDetails } from '../../../store/thunks/user.thunk';
import { setRedirectURL } from '../../../store/userSlice';
import { TokenCookieManager } from '../../../utils/tokenCookies';

init(process.env.REACT_APP_GOAFFPRO_PUBLIC_KEY!);

interface SignUpProps {
  signUpStep?: number;
  setSignUpStep?: (value: number) => void;
  plan?: string | null;
}

const GeneralSignUpContainer = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigateTo = useNavigate();
  const [fetchingUserDataAndAutoSubscribe, setFetchingUserDataAndAutoSubscribe] = useState<boolean>(false);
  const [tryToSubscribeFromInvitation, setTryToSubscribeFromInvitation] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  const user = useSelector((state: RootState) => state.user.user);
  const searchParams = getURLSearchParams();
  const userAuthorized = useSelector((state: RootState) => state.user.userAuthorized);

  useEffect(() => {
    if (user && userAuthorized) {
      dispatch(setRedirectURL('/'));
    }
  }, [dispatch, user, userAuthorized]);

  const autoSubscribeOnPlanOrInvite = async (): Promise<void> => {
    const planId = searchParams.get(CONFIG.HEADERS.SUBSCRIBE_PLAN_ID);
    const inviteAccessToken = searchParams.get(CONFIG.HEADERS.INVITE_ACCESS_KEY);
    const currencyCode = searchParams.get(CONFIG.HEADERS.CURRENCY);
    const user = await getUserAuthSession();

    if (!user) {
      TokenCookieManager.removeToken();
      localStorage.removeItem(UserLocalStorageKeysEnum.CURRENT_USER);
      return;
    }

    if (planId) {
      setLoader(true);
      if (user?.email) {
        autoSubscribeOnThePlan(planId, currencyCode, user, dispatch, navigateTo, () => setLoader(false));
      } else {
        setFetchingUserDataAndAutoSubscribe(true);
        dispatch(autoFetchUserDetails());
      }
    } else if (inviteAccessToken) {
      setLoader(true);
      if (user?.email) {
        autoSubscribeOnThePlanFromInvitationToken(inviteAccessToken, user, dispatch, navigateTo, () => {
          forceFetchUserDetails(() => {});
          setLoader(false);
        });
      } else {
        setTryToSubscribeFromInvitation(true);
        dispatch(forceFetchUserDetails(() => {}));
      }
    }
  };

  useEffect(() => {
    if (!TokenCookieManager.hasToken()) {
      return;
    }
    autoSubscribeOnPlanOrInvite().then().catch();
    return () => {
      setLoader(false);
    };
  }, []);

  useEffect(() => {
    if (!user?.email) {
      return;
    }
    if (fetchingUserDataAndAutoSubscribe) {
      setFetchingUserDataAndAutoSubscribe(false);
      const currencyCode = searchParams.get(CONFIG.HEADERS.CURRENCY);
      autoSubscribeOnThePlan(
        searchParams.get(CONFIG.HEADERS.SUBSCRIBE_PLAN_ID)!,
        currencyCode,
        user,
        dispatch,
        navigateTo,
        () => setLoader(false),
      );
    } else if (tryToSubscribeFromInvitation) {
      setTryToSubscribeFromInvitation(false);
      autoSubscribeOnThePlanFromInvitationToken(
        searchParams.get(CONFIG.HEADERS.INVITE_ACCESS_KEY)!,
        user,
        dispatch,
        navigateTo,
        () => {
          forceFetchUserDetails(() => {});
          setLoader(false);
        },
      );
    }
  }, [user]);

  return (
    <>
      {loader && (
        <div className="flex justify-center items-center">
          <Loader loader={true} />
        </div>
      )}
      {!loader && <GeneralSignUp signUpStep={1} openLoginPageEvent={() => navigateTo('/signIn')} />}
    </>
  );
};

export default GeneralSignUpContainer;
